import { useState } from 'react';
import { ArrowLeft, ArrowRight, Pencil, Trash2 } from 'lucide-react';
import { Button, EmptyState, ImagePickerButton } from '@/shared/components';
import { useUploadQueue } from '@/shared/hooks/useUploadQueue';
import { toastUploadOutcome } from '@/shared/lib/uploadSummary';
import { ArrivalImageCanvas } from './ArrivalImageCanvas';
import { ArrivalAnnotationEditor } from './ArrivalAnnotationEditor';
import { MAX_ARRIVAL_IMAGES, useArrivalImages } from './useArrivalImages';

/**
 * Bilder eines Schritts: hinzufügen, sortieren, markieren, entfernen. Ein Schritt braucht
 * selten mehr als zwei — die Reihenfolge ist die des Wegs, nicht die des Uploads.
 */
export function ArrivalImagePanel({ stepId, canEdit }: { stepId: string; canEdit: boolean }) {
  const { query, upload, remove, move, annotate } = useArrivalImages(stepId);
  const [editing, setEditing] = useState<string | null>(null);
  const queue = useUploadQueue();

  if (query.isLoading) return <p className="text-ink-soft text-sm">Lade Bilder…</p>;
  if (query.isError || !query.data) {
    return <EmptyState icon="Error" title="Bilder konnten nicht geladen werden" />;
  }

  const { images, palette } = query.data;
  const active = images.find((image) => image.id === editing);
  const addImages = async (files: File[]) => {
    const room = MAX_ARRIVAL_IMAGES - images.length;
    const outcome = await queue.run(files, room, (file) => upload.mutateAsync(file));
    toastUploadOutcome(outcome, `Höchstens ${MAX_ARRIVAL_IMAGES} Bilder je Schritt`);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-ink-soft text-sm">
          {images.length === 0
            ? 'Ein Foto der Stelle sagt mehr als drei Sätze.'
            : `${images.length} ${images.length === 1 ? 'Bild' : 'Bilder'} — Reihenfolge ist die des Wegs.`}
        </p>
        {canEdit && (
          <ImagePickerButton
            label="Bilder hinzufügen"
            progress={queue.progress}
            disabled={images.length >= MAX_ARRIVAL_IMAGES}
            onFiles={(files) => void addImages(files)}
          />
        )}
      </div>

      {images.length > 0 && (
        <ul className="flex flex-wrap gap-3">
          {images.map((image, index) => (
            <li key={image.id} className="w-40 space-y-1">
              <ArrivalImageCanvas
                stepId={stepId}
                imageId={image.id}
                variant="thumb"
                annotations={image.annotations}
                className="aspect-square w-full"
              />
              {canEdit && (
                <div className="flex items-center justify-between gap-1">
                  <div className="flex gap-0.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Bild nach vorn"
                      disabled={index === 0 || move.isPending}
                      onClick={() => move.mutate({ imageId: image.id, direction: 'Up' })}
                    >
                      <ArrowLeft size={13} strokeWidth={2} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Bild nach hinten"
                      disabled={index === images.length - 1 || move.isPending}
                      onClick={() => move.mutate({ imageId: image.id, direction: 'Down' })}
                    >
                      <ArrowRight size={13} strokeWidth={2} />
                    </Button>
                  </div>
                  <div className="flex gap-0.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Markierungen bearbeiten"
                      onClick={() => setEditing(image.id)}
                    >
                      <Pencil size={13} strokeWidth={2} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Bild entfernen"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(image.id)}
                    >
                      <Trash2 size={13} strokeWidth={2} />
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {active && (
        <ArrivalAnnotationEditor
          key={active.id}
          stepId={stepId}
          imageId={active.id}
          initial={active.annotations}
          palette={palette}
          saving={annotate.isPending}
          onClose={() => setEditing(null)}
          onSave={(annotations) =>
            annotate.mutate(
              { imageId: active.id, annotations },
              { onSuccess: () => setEditing(null) },
            )
          }
        />
      )}
    </div>
  );
}
